// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import OperatingManualContent from "../OperatingManualContent/OperatingManualContent";
import {
  BatteryFullIcon,
  BatteryLowIcon,
  BatteryMidIcon,
  BuildingIcon,
  CopyIcon,
  CrosshairIcon,
  EditIcon,
  HeadphonesIcon,
  HeadphonesOffIcon,
  LinkIcon,
  MaximizeIcon,
  MinimizeIcon,
  MusicIcon,
  MusicOffIcon,
  PlusIcon,
  QuestionCircleIcon,
  SearchIcon,
  SpeechBubbleIcon,
  SpeechBubbleOffIcon,
  UserEditIcon,
  UserIcon,
  ViewFileIcon,
  VRIcon,
  HeartbeatIcon,
} from "src/assets/icons";

import volumeSliderImg from "src/assets/images/operating-manual/appendix-f/volume-slider.png";
import controllerOnlineImg from "src/assets/images/operating-manual/appendix-f/controller-online.png";
import controllerOfflineImg from "src/assets/images/operating-manual/appendix-f/controller-offline.png";

// Styles
import {
  ModalCustomConfirm,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

type AppendixFType = {
  title: string;
};

const AppendixF = ({ title }: AppendixFType) => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();
  const { elevatedSurface: iconBackgroundColor } = getSurfaceTokens(theme);

  const iconContent = [
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <PlusIcon />
          </IconButton>
        ),
        text: "Add",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <SpeechBubbleIcon />
          </IconButton>
        ),
        text: "Add comment",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <SearchIcon />
          </IconButton>
        ),
        text: "Search",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <EditIcon />
          </IconButton>
        ),
        text: "Edit",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <UserIcon />
          </IconButton>
        ),
        text: "Profile",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <CopyIcon />
          </IconButton>
        ),
        text: "Duplicate exam settings",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <BuildingIcon />
          </IconButton>
        ),
        text: "Clinic (click to switch clinics)",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <UserEditIcon />
          </IconButton>
        ),
        text: "Reassign exam to a different patient",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <ViewFileIcon />
          </IconButton>
        ),
        text: "View results",
      },
      {
        icon: (
          <Button
            sx={{
              ...ModalCustomConfirm(),
              pointerEvents: "none",
              minWidth: "fit-content",
              padding: "8px",
            }}
          >
            <VRIcon />
            <LinkIcon />
          </Button>
        ),
        text: "Link device",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <HeartbeatIcon />
          </IconButton>
        ),
        text: "Live update panel",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#E2772E",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <BatteryLowIcon />
          </IconButton>
        ),
        text: "Device battery under 20%",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#E2772E",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <BatteryMidIcon />
          </IconButton>
        ),
        text: "Device battery under 60%",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#5B6D7A",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <BatteryFullIcon />
          </IconButton>
        ),
        text: "Device battery 60% or over",
      },
    ],
    [
      {
        icon: (
          <img
            src={volumeSliderImg}
            height="40px"
            width="80px"
            alt="volume-slider"
          />
        ),
        text: "HMD volume",
      },
      {
        icon: (
          <Button
            startIcon={<CrosshairIcon />}
            sx={{
              ...ModalCustomConfirm(),
              pointerEvents: "none",
              minWidth: "fit-content",
              pl: "10px",
              padding: "8px",
            }}
          >
            {t("Recenter")}
          </Button>
        ),
        text: "Recenter patient perspective",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <MusicIcon />
          </IconButton>
        ),
        text: "Controller beep sound enabled",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <MusicOffIcon />
          </IconButton>
        ),
        text: "Controller beep sound disabled",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <HeadphonesIcon />
          </IconButton>
        ),
        text: "Voice warnings enabled",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <HeadphonesOffIcon />
          </IconButton>
        ),
        text: "Voice warnings disabled",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <SpeechBubbleIcon />
          </IconButton>
        ),
        text: "Captions enabled",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <SpeechBubbleOffIcon />
          </IconButton>
        ),
        text: "Captions disabled",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <MaximizeIcon />
          </IconButton>
        ),
        text: "Maximize live update modal",
      },
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#000",
              backgroundColor: "#FFF",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <MinimizeIcon />
          </IconButton>
        ),
        text: "Minimize live update modal",
      },
    ],
    [
      {
        icon: (
          <img
            src={controllerOnlineImg}
            width="30px"
            alt="connected-controller"
          />
        ),
        text: "Controller is connected",
      },
      {
        icon: (
          <img
            src={controllerOfflineImg}
            width="30px"
            alt="offline-controller"
          />
        ),
        text: "Controller is offline",
      },
    ],
    [
      {
        icon: (
          <IconButton
            sx={{
              width: `${UI_SIZES.iconButtonSize}px`,
              height: `${UI_SIZES.iconButtonSize}px`,
              color: "#FFF",
              backgroundColor: iconBackgroundColor,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <QuestionCircleIcon />
          </IconButton>
        ),
        text: "Operating manual",
      },
    ],
  ];

  return (
    <OperatingManualContent title={title}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          mx: "20px",
        }}
      >
        {iconContent.map((iconArray) => (
          <Grid container spacing="20px">
            {iconArray.map((iconItem) => (
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "14px", sm: "16px" },
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  {iconItem.icon}
                  {t(iconItem.text)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        ))}
      </Box>
    </OperatingManualContent>
  );
};

export default AppendixF;
